// Voice modal state machine (docs/mockups/ai-voice-modal-mockup.html).
// Five states, four visuals, zero ambiguity: speaking is a loudspeaker, listening
// is a mic, and they can never be conflated. The reducer is pure — all side
// effects (start/stop recognition, stop TTS) live in the dashboard's dispatcher.
//
//   closed ── OPEN ──▶ listening ── TRANSCRIPT_FINAL ──▶ thinking ── REPLY_READY ──▶ speaking
//                          ▲                                 │ CANCEL/ERROR             │
//                          │◀───────── SPEAK ─────────── paused ◀───── STOP_AUDIO ──────┤
//                          │◀──────────────────────── AUDIO_ENDED ──────────────────────┘
//   any state ── DISMISS ──▶ closed  (swipe-down / scrim tap; audio + mic must die)

export const VOICE_EVENTS = {
  OPEN: 'OPEN',
  TRANSCRIPT_FINAL: 'TRANSCRIPT_FINAL',
  REPLY_READY: 'REPLY_READY',
  AUDIO_ENDED: 'AUDIO_ENDED',
  STOP_AUDIO: 'STOP_AUDIO',
  SPEAK: 'SPEAK',
  CANCEL: 'CANCEL',
  ERROR: 'ERROR',
  DISMISS: 'DISMISS',
};

const TRANSITIONS = {
  closed:    { OPEN: 'listening' },
  listening: { TRANSCRIPT_FINAL: 'thinking', ERROR: 'paused', DISMISS: 'closed' },
  // AUDIO_ENDED can arrive while still 'thinking' when TTS fails entirely (every
  // chunk errored) or the reply chunks to nothing — without this transition the
  // modal was stranded on "Thinking…" though the reply was already in the chat.
  thinking:  { REPLY_READY: 'speaking', CANCEL: 'paused', ERROR: 'paused', AUDIO_ENDED: 'listening', DISMISS: 'closed' },
  speaking:  { STOP_AUDIO: 'paused', SPEAK: 'listening', AUDIO_ENDED: 'listening', DISMISS: 'closed' },
  paused:    { SPEAK: 'listening', DISMISS: 'closed' },
};

export function voiceReducer(state, event) {
  const row = TRANSITIONS[state];
  return (row && row[event]) || state;
}

// Sentence chunking for pipelined TTS. The server synthesizes a WHOLE request to a
// file before responding, so first-sound latency scales with text length — a long
// reply meant ~20s of silence. Chunk 1 is the FIRST SENTENCE ALONE (fastest possible
// start); the rest group up to maxLen and prefetch while earlier chunks play.
export function ttsChunks(text, maxLen = 240) {
  const clean = String(text || '').trim();
  if (!clean) return [];
  const sentences = clean.match(/[^.!?\n]+[.!?]*[\s\n]*/g) || [clean];
  const pieces = [];
  sentences.forEach((s) => {
    let p = s;
    while (p.length > maxLen) { pieces.push(p.slice(0, maxLen)); p = p.slice(maxLen); }
    if (p.trim()) pieces.push(p);
  });
  if (!pieces.length) return [];
  // The sentence regex treats a list marker ("1.") as a sentence END — leaving a
  // chunk dangling on "…: 1." and starting the next mid-item. Move a trailing
  // marker onto the FOLLOWING piece — but ONLY when it really is a list marker:
  // the whole piece is the marker, or a colon introduces it. A normal sentence
  // ending in a number ("Your meeting is at 3.") must NOT be split.
  for (let i = 0; i < pieces.length - 1; i++) {
    const whole = pieces[i].match(/^\s*(\d+[.)])\s*$/);
    const afterColon = pieces[i].match(/:\s*(\d+[.)])\s*$/);
    const marker = whole ? whole[1] : (afterColon ? afterColon[1] : null);
    if (marker) {
      pieces[i] = whole ? '' : pieces[i].replace(/(\d+[.)])\s*$/, '');
      pieces[i + 1] = marker + ' ' + pieces[i + 1];
      if (!pieces[i].trim()) { pieces.splice(i, 1); i--; }
    }
  }
  const chunks = [pieces[0].trim()];
  let cur = '';
  pieces.slice(1).forEach((p) => {
    if (cur && (cur.length + p.length) > maxLen) { chunks.push(cur.trim()); cur = ''; }
    cur += p;
  });
  if (cur.trim()) chunks.push(cur.trim());
  return chunks;
}
